import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useTheme } from '../contexts/ThemeContext';
import { 
  FileText, 
  Users, 
  Shield, 
  Clock, 
  CheckCircle, 
  ArrowRight,
  Mail,
  Sun,
  Moon
} from 'lucide-react';

// Logo component using the brand image
const Logo = ({ className = "h-10", white = false }) => (
  <img 
    src="/logo.png"
    alt="NewSlate"
    className={`${className} object-contain`}
    style={white ? { filter: 'brightness(0) invert(1)' } : {}}
  />
);

export default function LandingPage() {
  const { isDark, toggleTheme } = useTheme();
  
  return (
    <div className="min-h-screen bg-background grain-texture">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass-header border-b border-border bg-card/95 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Logo className="h-12" />
          </div>
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="text-muted-foreground hover:text-foreground"
              data-testid="landing-theme-toggle-btn"
            >
              {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
            <Link to="/register">
              <Button className="btn-lift bg-accent text-white font-body" data-testid="nav-signup-btn">
                Start Free Trial
              </Button>
            </Link>
            <Link to="/login">
              <Button variant="outline" className="font-body border-primary text-primary hover:bg-primary hover:text-white" data-testid="nav-login-btn">
                Sign In
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section - Tetris Grid */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto grid grid-cols-12 gap-6">
          {/* Main Hero Content */}
          <div className="col-span-12 md:col-span-5 flex flex-col justify-center">
            <p className="text-sm uppercase tracking-widest text-accent font-body font-semibold mb-4">
              Immigration Case Management
            </p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground tracking-tight leading-tight mb-6">
              Simplify Your<br />
              <span className="text-primary">Immigration</span><br />
              Practice
            </h1>
            <p className="font-body text-base text-muted-foreground mb-8 max-w-md">
              The complete CRM solution for emigration companies. Track cases, manage documents, 
              and keep clients informed — all in one secure platform.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/register">
                <Button 
                  size="lg" 
                  className="btn-lift bg-primary text-white font-body px-8"
                  data-testid="hero-get-started-btn"
                >
                  Start Free Trial
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <a href="https://newslate.io/api/docs/ADVERTISEMENT.html">
                <Button 
                  variant="outline" 
                  size="lg" 
                  className="font-body border-primary text-primary hover:bg-primary hover:text-white"
                  data-testid="hero-learn-more-btn"
                >
                  Learn More
                </Button>
              </a>
              <Link to="/pricing">
                <Button 
                  variant="outline" 
                  size="lg" 
                  className="font-body border-primary text-primary hover:bg-primary hover:text-white"
                  data-testid="hero-pricing-btn"
                >
                  View Pricing
                </Button>
              </Link>
            </div>
          </div>

          {/* Hero Image */}
          <div className="col-span-12 md:col-span-7 relative">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src="https://images.unsplash.com/photo-1563809717482-846b98818bad?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwyfHxhaXJwb3J0JTIwdGVybWluYWwlMjBpbnRlcmlvciUyMHBhc3NlbmdlcnN8ZW58MHx8fHwxNzcwMjM3NzU4fDA&ixlib=rb-4.1.0&q=85" 
                alt="Modern airport terminal"
                className="w-full h-[400px] md:h-[500px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/30 to-transparent" />
            </div>
            {/* Floating Stats Card */}
            <div className="absolute -bottom-6 -left-6 bg-card rounded-xl p-6 shadow-xl border border-border">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-success/10 flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-success" />
                </div>
                <div>
                  <p className="font-heading text-2xl font-bold text-foreground">2,500+</p>
                  <p className="font-body text-sm text-muted-foreground">Cases Managed</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-6 bg-secondary/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-widest text-accent font-body font-semibold mb-3">
              Features
            </p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground tracking-tight">
              Everything You Need to Manage Cases
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 animate-stagger">
            {/* Feature Card 1 */}
            <div className="bg-card rounded-xl p-6 border border-border card-hover" data-testid="feature-client-management">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                <Users className="w-6 h-6 text-primary" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-lg font-semibold text-foreground mb-2">
                Client Management
              </h3>
              <p className="font-body text-sm text-muted-foreground">
                Comprehensive profiles with all USCIS-required fields, contact info, and case history.
              </p>
            </div>

            {/* Feature Card 2 */}
            <div className="bg-card rounded-xl p-6 border border-border card-hover" data-testid="feature-document-storage">
              <div className="w-12 h-12 rounded-lg bg-accent/20 flex items-center justify-center mb-4">
                <FileText className="w-6 h-6 text-accent" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-lg font-semibold text-foreground mb-2">
                Document Storage
              </h3>
              <p className="font-body text-sm text-muted-foreground">
                Securely upload and manage passports, forms, and supporting documents per client.
              </p>
            </div>

            {/* Feature Card 3 */}
            <div className="bg-card rounded-xl p-6 border border-border card-hover" data-testid="feature-status-tracking">
              <div className="w-12 h-12 rounded-lg bg-success/10 flex items-center justify-center mb-4">
                <Clock className="w-6 h-6 text-success" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-lg font-semibold text-foreground mb-2">
                Status Tracking
              </h3>
              <p className="font-body text-sm text-muted-foreground">
                Real-time updates on case progress, USCIS decisions, and important dates.
              </p>
            </div>

            {/* Feature Card 4 */}
            <div className="bg-card rounded-xl p-6 border border-border card-hover" data-testid="feature-secure-platform">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-primary" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-lg font-semibold text-foreground mb-2">
                Secure Platform
              </h3>
              <p className="font-body text-sm text-muted-foreground">
                Enterprise-grade security to protect sensitive client information and documents.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-primary rounded-2xl p-12 text-center relative overflow-hidden">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 right-0 w-64 h-64 bg-card rounded-full -translate-y-1/2 translate-x-1/2" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-card rounded-full translate-y-1/2 -translate-x-1/2" />
            </div>
            <div className="relative z-10">
              <h2 className="font-display text-3xl md:text-4xl font-bold text-white tracking-tight mb-4">
                Ready to Transform Your Practice?
              </h2>
              <p className="font-body text-base text-white/80 mb-8 max-w-lg mx-auto">
                Join hundreds of immigration professionals who trust NewSlate to manage their cases efficiently.
              </p>
              <Link to="/register">
                <Button 
                  size="lg" 
                  className="bg-accent text-white font-body px-8 btn-lift hover:bg-accent/90"
                  data-testid="cta-start-btn"
                >
                  Start Your Free Trial
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-[#1f3b70] text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center gap-2 mb-4">
                <Logo className="h-14" white />
              </div>
              <p className="font-body text-sm text-white/70 max-w-sm">
                The trusted CRM solution for immigration professionals. Streamline your practice and focus on what matters — helping people start new chapters in their lives.
              </p>
            </div>
            <div>
              <h4 className="font-heading font-semibold mb-4 text-accent">Contact</h4>
              <div className="space-y-2 text-sm text-white/70 font-body">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  <span>support@newslate.io</span>
                </div>
              </div>
            </div>
            <div>
              <h4 className="font-heading font-semibold mb-4 text-accent">Quick Links</h4>
              <div className="space-y-2 text-sm text-white/70 font-body">
                <Link to="/register" className="block hover:text-accent transition-colors">Start Free Trial</Link>
                <Link to="/login" className="block hover:text-accent transition-colors">Sign In</Link>
                <Link to="/pricing" className="block hover:text-accent transition-colors">Pricing</Link>
                <Link to="/faq" className="block hover:text-accent transition-colors">FAQ</Link>
                <a href="https://newslate.io/api/docs/CONTACT.html?type=trial" className="block hover:text-accent transition-colors" data-testid="footer-contact-link">Contact Us</a>
                <a href="https://newslate.io/api/docs/ADVERTISEMENT.html" className="block hover:text-accent transition-colors" data-testid="footer-about-link">About NewSlate</a>
              </div>
            </div>
          </div>
          <div className="pt-8 border-t border-white/10 text-center">
            <p className="font-body text-sm text-white/50">
              © 2025 NewSlate. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
